/**
 * Taken, CommonJS-ified, and heavily modified from:
 * https://github.com/flyingsparx/NodeDirectUploader
 * Refactored to use ES6 Class syntax.
 */

class AsyncS3Uploader {
    // Default instance properties (using class fields)
    server = '';
    signingUrl = '/sign-s3';
    signingUrlMethod = 'GET';
    signingUrlWithCredentials = false; // Added: Assumed default based on usage
    signingUrlHeaders = null;          // Added: Assumed default based on usage
    signingUrlQueryParams = null;      // Added: Assumed default based on usage
    s3path = '';                       // Added: Assumed default based on usage
    contentDisposition = 'auto';       // Added: Assumed default based on usage
    uploadRequestHeaders = { 'x-amz-acl': 'public-read' };       // Added: Assumed default based on usage
    successResponses = [200, 201];
    files = null; // User can pass files directly via options
    getSignedUrl = null; // Optional custom function for signing
    httprequest = null; // To hold the XHR request for aborting


    // Preprocess hook, receives file and a 'next' callback.
    // Call next(processedFile) to continue, or next() / next(null) to skip.
    preprocess = async (file) => {
        console.log('base.preprocess()', file.name);
        return file; // Continue with the original file by default
    };

    onProgress = (percent, status, file) => {
        console.log('base.onProgress()', percent, status, file.name);
    };

    // Called after successfully getting the signed URL, before the PUT request.
    onSignedUrl = (result) => {
        console.log('base.onSignedUrl(): Signed URL obtained.');
    };

    // --- Core Methods ---

    constructor(options = {}) {
        // Apply user options, overriding defaults defined above
        Object.assign(this, options);
    }

    scrubFilename(filename) {
        return filename.replace(/[^\w\d_\-\.]+/ig, '');
    }

    async uploadFiles(files) {
        // Use Array.from for compatibility with FileList which isn't a true array
        return Promise.all(Array.from(files).map(async (file) => {
            // Use arrow function for the preprocess callback to maintain 'this' context
            const processedFile = await this.preprocess(file);
            // Check if preprocess returned a file (it might skip by returning null/undefined)
            if (processedFile) {
                this.onProgress(0, 'Waiting', processedFile);
                return this.uploadFile(processedFile);
            } else {
                console.log(`S3Upload: File skipped during preprocessing: ${file.name}`);
            }
        }));
    }

    createCORSRequest(method, url, opts = {}) {
        let xhr = new XMLHttpRequest();
        if ('withCredentials' in xhr) {
            // Standard CORS
            xhr.open(method, url, true);
            // Check if the property exists in opts before setting
            if (opts.hasOwnProperty('withCredentials')) {
                xhr.withCredentials = opts.withCredentials;
            }
            // @ts-ignore
        } else if (typeof XDomainRequest !== "undefined") {
            // IE specific CORS (limited functionality)
            // @ts-ignore
            xhr = new XDomainRequest();
            xhr.open(method, url);
        } else {
            // CORS not supported
            throw new Error('CORS not supported by browser');            
        }
        return xhr;
    }

    async executeOnSignedUrl(file) {
        const fileName = this.scrubFilename(file.name);
        let queryString = `?objectName=${fileName}&contentType=${encodeURIComponent(file.type)}`;

        if (this.s3path) {
            queryString += `&path=${encodeURIComponent(this.s3path)}`;
        }

        // Handle signingUrlQueryParams (can be an object or a function returning an object)
        const queryParams = typeof this.signingUrlQueryParams === 'function'
            ? this.signingUrlQueryParams(file) // Pass file context if it's a function
            : this.signingUrlQueryParams;
        if (queryParams) {
            Object.keys(queryParams).forEach(key => {
                queryString += `&${key}=${encodeURIComponent(queryParams[key])}`;
            });
        }

        const url = this.server + this.signingUrl + queryString;
        const xhr = this.createCORSRequest(
            this.signingUrlMethod,
            url,
            { withCredentials: this.signingUrlWithCredentials } // Pass credentials option
        );

        if (!xhr) {
            // Error already logged in createCORSRequest
            return;
        }

        // Handle signingUrlHeaders (can be an object or a function returning an object)
        const headers = typeof this.signingUrlHeaders === 'function'
            ? this.signingUrlHeaders(file) // Pass file context if it's a function
            : this.signingUrlHeaders;
        if (headers) {
            Object.keys(headers).forEach(key => {
                xhr.setRequestHeader(key, headers[key]);
            });
        }

        return new Promise((resolve, reject) => {
            // Expect a JSON response
            xhr.responseType = 'json';
            // xhr.overrideMimeType && xhr.overrideMimeType('application/json'); // Often helpful

            // Use arrow function for onreadystatechange to retain 'this'
            xhr.onreadystatechange = () => {
                if (xhr.readyState === 4) {
                    if (this.successResponses.indexOf(xhr.status) >= 0) {
                        let result;
                        try {
                            // xhr.response should hold the parsed JSON if responseType='json' worked
                            result = xhr.response || JSON.parse(xhr.responseText);
                            if (!result || !result.signedUrl) {
                                throw new Error("Response missing 'signedUrl'");
                            }
                            this.onSignedUrl(result); // Call user hook
                            resolve(result);        // Proceed with the upload
                        } catch (error) {
                            console.error("S3Upload: Error parsing signing server response.", error, xhr.responseText);
                            reject(new Error(`Invalid response from signing server: ${error.message}`));
                        }
                    } else {
                        console.error(`S3Upload: Sign URL request failed. Status: ${xhr.status}, Response: ${xhr.responseText}`);
                        reject(new Error(`Signing server request failed: ${xhr.status}`));
                    }
                }
            };

            // Handle network errors for the signing request
            xhr.onerror = () => {
                console.error("S3Upload: Network error during sign URL request.");
                reject(new Error('Network error contacting signing server'));
            };

            xhr.send(this.signingUrlMethod === 'POST' ? JSON.stringify({ // Example: Send body if POST
                objectName: fileName,
                contentType: file.type,
                path: this.s3path
            }) : null);
        });
    }

    async uploadToS3(file, signResult) {
        return new Promise((resolve, reject) => {
            // Use signedUrl from the result provided by executeOnSignedUrl/getSignedUrl
            const xhr = this.createCORSRequest('PUT', signResult.signedUrl);
            if (!xhr) {
                // Error logged in createCORSRequest
                return;
            }

            this.httprequest = xhr; // Store the request for potential abortion

            // Use arrow functions for handlers to retain 'this'
            xhr.onload = () => {
                this.httprequest = null; // Clear request object on completion
                if (this.successResponses.indexOf(xhr.status) >= 0) {
                    this.onProgress(100, 'Upload completed', file);
                     console.log('Upload completed', signResult.publicUrl, file.name);
                    resolve(signResult);
                } else {
                    console.error(`S3Upload: S3 PUT failed. Status: ${xhr.status}, Response: ${xhr.responseText}`);
                    reject(new Error(`Upload error: ${xhr.status}`));
                }
            };

            xhr.onerror = () => {
                this.httprequest = null; // Clear request object on error
                console.error("S3Upload: Network error during S3 PUT.", file);
                reject(new Error('XHR network error during upload'));
            };

            if (xhr.upload) { // Check if upload property exists for progress tracking
                xhr.upload.onprogress = (e) => {
                    if (e.lengthComputable) {
                        const percentLoaded = Math.round((e.loaded / e.total) * 100);
                        this.onProgress(percentLoaded, percentLoaded === 100 ? 'Finalizing' : 'Uploading', file);
                    } else {
                        // Progress is not computable (often happens with very small files or certain server configs)
                        this.onProgress(-1, 'Uploading', file); // Indicate indeterminate progress
                    }
                };
            }

            // --- Set Headers ---
            xhr.setRequestHeader('Content-Type', file.type);

            // Content Disposition
            let disposition = this.contentDisposition;
            if (disposition === 'auto') {
                disposition = file.type.startsWith('image/') ? 'inline' : 'attachment';
            }
            if (disposition === 'inline' || disposition === 'attachment') {
                const safeFilename = this.scrubFilename(file.name);
                xhr.setRequestHeader('Content-Disposition', `${disposition}; filename="${safeFilename}"`);
            }

            // Headers from the signing response (e.g., AWS temporary security headers)
            if (signResult.headers) {
                Object.keys(signResult.headers).forEach(key => {
                    xhr.setRequestHeader(key, signResult.headers[key]);
                });
            }

            // Custom upload headers (can be object or function, potentially overriding defaults like ACL)
            const customHeaders = typeof this.uploadRequestHeaders === 'function'
                ? this.uploadRequestHeaders(file, signResult) // Pass file & signResult context
                : this.uploadRequestHeaders;
            if (customHeaders) {
                Object.keys(customHeaders).forEach(key => {
                    xhr.setRequestHeader(key, customHeaders[key]);
                });
            } else if (!signResult.headers || !signResult.headers['x-amz-acl']) {
                // Set default ACL *only if* no custom headers are provided *and*
                // the signing result didn't already include an ACL header.
                xhr.setRequestHeader('x-amz-acl', 'public-read');
            }

            xhr.send(file);
        });
    }

    async uploadFile(file) {
        // Allow user to provide their own method for getting the signed URL
        let signResult;
        if (typeof this.getSignedUrl === 'function') {
            try {
                // The user's function is responsible for calling the callback
                signResult = await this.getSignedUrl(file);
            } catch (error) {
                console.error("S3Upload: Error executing custom getSignedUrl function.", error);
                throw new Error(`Custom getSignedUrl error: ${error.message}`);
            }
        } else {
            // Default behavior: get the signed URL from the configured endpoint
            signResult = await this.executeOnSignedUrl(file);
        }

        // Basic validation of the signResult before proceeding
        if (signResult && signResult.signedUrl) {
            return this.uploadToS3(file, signResult);
        } else {
            console.error("S3Upload: Invalid signResult received.", signResult);
            throw new Error("Failed to get valid signed URL structure");
        }
    }

    abortUpload() {
        if (this.httprequest) {
            this.httprequest.abort();
            this.httprequest = null; // Clear the stored request
            console.log("S3Upload: Upload aborted by user.");
        } else {
            console.log("S3Upload: Abort called but no active HTTP request found.");
        }
    }
}

export default AsyncS3Uploader;